import type { PrimaryEntityId } from "building-types/doc/primaryEntityId";
import type { DocEntityBase } from "building-types/entities/docEntityBase";
import type { ExteriorMaterial } from "building-types/materials/exteriorMaterialTypes";
import type { PolyLine2 } from "geom/polyLine2Types";
import type {
  FootingType,
  BrickLedgeType,
  BrickLedgeBehavior,
  PierType,
  StemWallType,
  CurbType,
} from "./foundationEnums";

export enum FoundationComponentType {
  FOOTING = "FOOTING",
  STEMWALL = "STEMWALL",
  CURB = "CURB",
  PIER = "PIER",
}

export interface FoundationComponentBase<T extends FoundationComponentType>
  extends DocEntityBase<"FOUNDATION_COMPONENT"> {
  readonly id: PrimaryEntityId<"FOUNDATION_COMPONENT">;
  readonly name: string;
  readonly componentType: T;
  readonly material?: ExteriorMaterial;
}

export type FoundationComponentEntity = FootingEntity | StemWallEntity | CurbEntity | PierEntity;

// Footing

export type FootingEntity =
  | TurndownFootingEntity
  | StripFootingEntity
  | CircularIsolatedFootingEntity
  | RectangularIsolatedFootingEntity;

export interface FootingBase<T extends FootingType> extends FoundationComponentBase<FoundationComponentType.FOOTING> {
  readonly footingType: T;
}

export interface StripFootingEntity extends FootingBase<FootingType.STRIP> {
  readonly footingWidth: number;
  readonly footingDepth: number;
  readonly footingPadding: number;
  readonly lateralOffset?: number;
}

export interface CircularIsolatedFootingEntity extends FootingBase<FootingType.CIRCULAR_ISOLATED> {
  readonly footingRadius: number;
  readonly footingDepth: number;
}

export interface RectangularIsolatedFootingEntity extends FootingBase<FootingType.RECTANGULAR_ISOLATED> {
  readonly footingLength: number;
  readonly footingWidth: number;
  readonly footingDepth: number;
}

export type TurndownBrickLedgeType = BrickLedgeType.ADD | BrickLedgeType.NONE;

export interface TurndownFootingEntity extends FootingBase<FootingType.TURNDOWN> {
  readonly footingWidth: number;
  readonly brickLedgeBehavior: BrickLedgeBehavior;
  readonly brickLedgeType: TurndownBrickLedgeType;
  readonly brickLedgeWidth: number;
}

// Pier
export type PierEntity =
  | CircularCMUPierEntity
  | RectangularCMUPierEntity
  | CustomPierEntity
  | CircularCIPPierEntity
  | RectangularCIPPierEntity;

interface PierBase<T extends PierType> extends FoundationComponentBase<FoundationComponentType.PIER> {
  readonly pierType: T;
}

export interface CircularCMUPierEntity extends PierBase<PierType.CIRCULAR_CMU> {
  readonly pierRadius: number;
  readonly pierDepth?: number;
}

export interface RectangularCMUPierEntity extends PierBase<PierType.RECTANGULAR_CMU> {
  readonly pierLength: number;
  readonly pierWidth: number;
  readonly pierDepth?: number;
}

export interface CircularCIPPierEntity extends PierBase<PierType.CIRCULAR_CIP> {
  readonly pierRadius: number;
  readonly pierDepth?: number;
}

export interface RectangularCIPPierEntity extends PierBase<PierType.RECTANGULAR_CIP> {
  readonly pierLength: number;
  readonly pierWidth: number;
  readonly pierDepth?: number;
}

export interface CustomPierEntity extends PierBase<PierType.CUSTOM> {
  readonly pierPoints: PolyLine2;
}

// Stem Wall

export type StemWallEntity = CIPStemWallEntity | CMUStemWallEntity;

interface StemWallBase<T extends StemWallType> extends FoundationComponentBase<FoundationComponentType.STEMWALL> {
  readonly stemWallType: T;
}

export type CIPStemWallBrickLedgeType = BrickLedgeType;

export interface CIPStemWallEntity extends StemWallBase<StemWallType.CIP> {
  readonly stemWallWidth: number;
  readonly brickLedgeBehavior: BrickLedgeBehavior;
  readonly brickLedgeType: CIPStemWallBrickLedgeType;
  readonly brickLedgeWidth: number;
}

export type CMUBrickLedgeType = BrickLedgeType.ADD | BrickLedgeType.NONE;

export interface CMUStemWallEntity extends StemWallBase<StemWallType.CMU> {
  readonly stemWallBlockWidth: number;
  readonly brickLedgeBehavior: BrickLedgeBehavior;
  readonly brickLedgeType: CMUBrickLedgeType;
  readonly stemWallBrickLedgeBlockWidth: number;
}

// Curb

export type CurbEntity = FixedWidthCurbEntity | AutoWidthCurbEntity;

interface CurbBase<T extends CurbType> extends FoundationComponentBase<FoundationComponentType.CURB> {
  readonly curbType: T;
}

export interface FixedWidthCurbEntity extends CurbBase<CurbType.FIXED_WIDTH> {
  readonly curbWidth: number;
}

export type AutoWidthCurbEntity = CurbBase<CurbType.AUTO_WIDTH>;

export type FoundationComponentId = FoundationComponentEntity["id"];

export type FoundationComponentEntityTable = {
  [id: FoundationComponentId]: FoundationComponentEntity;
};
